import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  Diamond as DiamondIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import type { HostSubscriptionInfo, GrantSubscriptionFormData } from '../types/subscription.types';
import { useActivateSubscription, useCurrentPricing } from '../hooks/useFleetSubscriptions';
import { formatNaira, calculateDiscounts } from '../../../services/fleetSubscriptionService';

interface GrantSubscriptionDialogProps {
  open: boolean;
  host: HostSubscriptionInfo | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const validationSchema = yup.object({
  duration_months: yup.number().oneOf([1, 3, 12]).required('Duration is required'),
  notes: yup
    .string()
    .required('Reason/notes are required')
    .min(10, 'Notes must be at least 10 characters'),
});

const GrantSubscriptionDialog: React.FC<GrantSubscriptionDialogProps> = ({
  open,
  host,
  onClose,
  onSuccess,
}) => {
  const { data: pricing, isLoading: pricingLoading } = useCurrentPricing();
  const activateSubscription = useActivateSubscription();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GrantSubscriptionFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      duration_months: 12,
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        duration_months: 12,
        notes: '',
      });
    }
  }, [open, reset]);

  const getPriceForDuration = (months: number) => {
    if (!pricing) return 0;

    switch (months) {
      case 1:
        return pricing.monthly_rate;
      case 3:
        return pricing.quarterly_rate;
      case 12:
        return pricing.yearly_rate;
      default:
        return 0;
    }
  };

  const getDiscountPercent = (months: number) => {
    if (!pricing || months === 1) return 0;

    const discounts = calculateDiscounts(pricing);

    if (months === 3) return discounts.quarterly_discount_percent;
    if (months === 12) return discounts.yearly_discount_percent;

    return 0;
  };

  const onSubmit = async (data: GrantSubscriptionFormData) => {
    if (!host) return;

    try {
      await activateSubscription.mutateAsync({
        userId: host.user_id,
        data: {
          duration_months: data.duration_months,
          notes: data.notes,
        },
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error('Activation error:', error);
    }
  };

  const handleClose = () => {
    if (!activateSubscription.isPending) {
      onClose();
    }
  };

  if (!host) return null;

  const isActive = host.active_subscription?.status === 'active';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DiamondIcon sx={{ color: '#9333ea' }} />
          <Typography variant="h6">
            {isActive ? 'Extend Premium Subscription' : 'Grant Premium Subscription'}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* Host Info */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Host Information
            </Typography>
            <Typography variant="h6">{host.full_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {host.user_id} • {host.email}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={host.host_type === 'fleet_manager' ? 'Fleet Manager' : 'Individual'}
                size="small"
                color={host.host_type === 'fleet_manager' ? 'primary' : 'default'}
              />
            </Box>
          </Box>

          {/* Duration Selection */}
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Subscription Duration *
          </Typography>

          <Controller
            name="duration_months"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field} value={field.value.toString()}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {[
                    { months: 1, label: '1 Month' },
                    { months: 3, label: '3 Months (Quarterly)' },
                    { months: 12, label: '12 Months (Yearly)' },
                  ].map((option) => {
                    const price = getPriceForDuration(option.months);
                    const discount = getDiscountPercent(option.months);

                    return (
                      <Box
                        key={option.months}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor:
                            field.value === option.months ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          backgroundColor:
                            field.value === option.months ? 'action.selected' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover',
                          },
                        }}
                        onClick={() => field.onChange(option.months)}
                      >
                        <FormControlLabel
                          value={option.months.toString()}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {option.label}
                                </Typography>
                                {discount > 0 && (
                                  <Chip
                                    label={`${discount}% off`}
                                    size="small"
                                    color="success"
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 700, color: 'primary.main' }}
                              >
                                {pricingLoading ? '...' : formatNaira(price)}
                              </Typography>
                            </Box>
                          }
                          sx={{ m: 0, width: '100%' }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </RadioGroup>
            )}
          />

          {errors.duration_months && (
            <Typography variant="caption" color="error">
              {errors.duration_months.message}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Notes Field */}
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Reason/Notes *"
                multiline
                rows={3}
                fullWidth
                placeholder="e.g., Promotional campaign Q1 2024, Customer loyalty reward, etc."
                error={!!errors.notes}
                helperText={errors.notes?.message || 'Explain why this subscription is being granted'}
              />
            )}
          />

          {/* Info Alert */}
          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
            {host.host_type === 'individual' ? (
              <Typography variant="body2">
                This will automatically promote <strong>{host.full_name}</strong> to{' '}
                <strong>Fleet Manager</strong> status.
              </Typography>
            ) : (
              <Typography variant="body2">
                {isActive
                  ? 'This will extend the current subscription period.'
                  : 'This will activate a new premium subscription.'}
              </Typography>
            )}
          </Alert>

          {/* Error Alert */}
          {activateSubscription.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {activateSubscription.error?.message || 'Failed to activate subscription'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={activateSubscription.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={activateSubscription.isPending || pricingLoading}
            startIcon={activateSubscription.isPending ? <CircularProgress size={20} /> : <DiamondIcon />}
          >
            {activateSubscription.isPending
              ? 'Activating...'
              : isActive
              ? 'Extend Subscription'
              : 'Grant Access'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GrantSubscriptionDialog;
